import { PhysicsObject, FunctionEntry, GraphSettings } from '@/types/simulation';
import { parseAndEvaluate, getDerivative } from '@/utils/mathParser';

const TRAIL_MAX = 120;
// 物体数に応じてサブステップを動的に決定
function getSubsteps(n: number): number {
  if (n <= 1) return 8;
  if (n <= 3) return 5;
  return 3;
}

interface CanvasCoordSystem {
  settings: GraphSettings;
  width: number;
  height: number;
}

export function worldToCanvas(
  wx: number,
  wy: number,
  { settings, width, height }: CanvasCoordSystem
): { cx: number; cy: number } {
  const { xMin, xMax, yMin, yMax } = settings;
  const cx = ((wx - xMin) / (xMax - xMin)) * width;
  const cy = height - ((wy - yMin) / (yMax - yMin)) * height;
  return { cx, cy };
}

export function canvasToWorld(
  cx: number,
  cy: number,
  { settings, width, height }: CanvasCoordSystem
): { wx: number; wy: number } {
  const { xMin, xMax, yMin, yMax } = settings;
  const wx = xMin + (cx / width) * (xMax - xMin);
  const wy = yMin + ((height - cy) / height) * (yMax - yMin);
  return { wx, wy };
}

export function getScale({ settings, width, height }: CanvasCoordSystem): { sx: number; sy: number } {
  return {
    sx: width / (settings.xMax - settings.xMin),
    sy: height / (settings.yMax - settings.yMin),
  };
}

export function objectRadiusWorld(obj: PhysicsObject): number {
  const baseRadius = obj.size / 2;
  if (obj.shape === 'triangle') return baseRadius * TRIANGLE_SCALE;
  return baseRadius;
}

export const TRIANGLE_SCALE = 1.35;

// ---- グラフ曲線との衝突検出 ─────────────────────────────────────

interface CollisionInfo {
  slope: number;
  normalX: number;
  normalY: number;
  penetration: number;
  surfaceY: number;
}

function getVertices(obj: PhysicsObject): { x: number; y: number }[] {
  const cos = Math.cos(obj.angle);
  const sin = Math.sin(obj.angle);
  const r = objectRadiusWorld(obj);

  if (obj.shape === 'square') {
    return [
      { x: -r, y: -r }, { x: r, y: -r },
      { x: r, y: r },   { x: -r, y: r },
    ].map((v) => ({
      x: obj.x + v.x * cos - v.y * sin,
      y: obj.y + v.x * sin + v.y * cos,
    }));
  }

  if (obj.shape === 'triangle') {
    return [
      { x: 0, y: -r },
      { x: r * 0.866, y: r * 0.5 },
      { x: -r * 0.866, y: r * 0.5 },
    ].map((v) => ({
      x: obj.x + v.x * cos - v.y * sin,
      y: obj.y + v.x * sin + v.y * cos,
    }));
  }

  return [];
}

function findCollisionAtPoint(
  px: number,
  py: number,
  functions: FunctionEntry[]
): CollisionInfo | null {
  const activeFuncs = functions.filter((f) => f.visible && !f.error && f.expression.trim());
  if (activeFuncs.length === 0) return null;

  let best: CollisionInfo | null = null;
  let maxPen = -Infinity;

  for (const fn of activeFuncs) {
    const graphY = parseAndEvaluate(fn.expression, px);
    if (isNaN(graphY) || !isFinite(graphY)) continue;
    const slope = getDerivative(fn.expression, px);
    if (isNaN(slope) || !isFinite(slope)) continue;

    const len = Math.sqrt(1 + slope * slope);
    const normalX = -slope / len;
    const normalY = 1 / len;
    const signedDist = (py - graphY) * normalY;

    if (signedDist < 0) {
      const pen = -signedDist;
      if (pen > maxPen) {
        maxPen = pen;
        best = { slope, normalX, normalY, penetration: pen, surfaceY: graphY };
      }
    }
  }
  return best;
}

function findCollisionCircle(
  cx: number,
  cy: number,
  radius: number,
  functions: FunctionEntry[]
): CollisionInfo | null {
  const activeFuncs = functions.filter((f) => f.visible && !f.error && f.expression.trim());
  if (activeFuncs.length === 0) return null;

  let best: CollisionInfo | null = null;
  let maxPen = -Infinity;

  for (const fn of activeFuncs) {
    let left = cx - radius * 3;
    let right = cx + radius * 3;

    for (let i = 0; i < 12; i++) {
      const m1 = left + (right - left) * 0.382;
      const m2 = left + (right - left) * 0.618;
      const y1 = parseAndEvaluate(fn.expression, m1);
      const y2 = parseAndEvaluate(fn.expression, m2);
      if (isNaN(y1) || isNaN(y2)) break;
      if ((m1 - cx) ** 2 + (y1 - cy) ** 2 < (m2 - cx) ** 2 + (y2 - cy) ** 2) right = m2;
      else left = m1;
    }

    const nearX = (left + right) / 2;
    const nearY = parseAndEvaluate(fn.expression, nearX);
    if (isNaN(nearY) || !isFinite(nearY)) continue;

    const dx = cx - nearX;
    const dy = cy - nearY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1e-10) continue;

    const slope = getDerivative(fn.expression, nearX);
    if (isNaN(slope) || !isFinite(slope)) continue;

    const len = Math.sqrt(1 + slope * slope);
    const normalX = -slope / len;
    const normalY = 1 / len;
    const signedDist = dx * normalX + dy * normalY;
    const pen = radius - signedDist;

    if (pen > 0 && pen > maxPen) {
      maxPen = pen;
      best = { slope, normalX, normalY, penetration: pen, surfaceY: nearY };
    }
  }
  return best;
}

function findCollisionPolygon(obj: PhysicsObject, functions: FunctionEntry[]): CollisionInfo | null {
  const vertices = getVertices(obj);
  if (vertices.length === 0) return null;

  let best: CollisionInfo | null = null;
  let maxPen = -Infinity;

  for (const v of vertices) {
    const col = findCollisionAtPoint(v.x, v.y, functions);
    if (col && col.penetration > maxPen) { maxPen = col.penetration; best = col; }
  }
  for (let i = 0; i < vertices.length; i++) {
    const j = (i + 1) % vertices.length;
    const col = findCollisionAtPoint(
      (vertices[i].x + vertices[j].x) / 2,
      (vertices[i].y + vertices[j].y) / 2,
      functions
    );
    if (col && col.penetration > maxPen) { maxPen = col.penetration; best = col; }
  }
  return best;
}

// ---- 物理サブステップ ─────────────────────────────────────────────

interface GraphContact {
  hasContact: boolean;
  info: CollisionInfo | null;
}

function checkGraphContact(
  obj: PhysicsObject,
  x: number,
  y: number,
  angle: number,
  functions: FunctionEntry[]
): GraphContact {
  const radius = objectRadiusWorld(obj);
  const tempObj = { ...obj, x, y, angle };
  let col: CollisionInfo | null = null;
  if (obj.shape === 'circle') {
    col = findCollisionCircle(x, y, radius, functions);
  } else {
    col = findCollisionPolygon(tempObj, functions);
  }
  return { hasContact: col !== null, info: col };
}

function resolveGraphCollision(
  obj: PhysicsObject,
  x: number,
  y: number,
  vx: number,
  vy: number,
  angVel: number,
  functions: FunctionEntry[]
): { x: number; y: number; vx: number; vy: number; angVel: number } {
  const radius = objectRadiusWorld(obj);
  const tempObj = { ...obj, x, y, angle: obj.angle };
  let col: CollisionInfo | null = null;
  if (obj.shape === 'circle') {
    col = findCollisionCircle(x, y, radius, functions);
  } else {
    col = findCollisionPolygon(tempObj, functions);
  }

  if (col === null) return { x, y, vx, vy, angVel };

  // 位置を強く押し出す（1.2倍のオーバーコレクション）
  const pushX = col.normalX * col.penetration * 1.2;
  const pushY = col.normalY * col.penetration * 1.2;
  const newX = x + pushX;
  const newY = y + pushY;

  // 速度の法線成分を反発
  const vn = vx * col.normalX + vy * col.normalY;
  let newVx = vx;
  let newVy = vy;
  let newAngVel = angVel;

  if (vn < 0) {
    const restitution = obj.restitution;
    const friction = obj.friction;

    const vnx = vn * col.normalX;
    const vny = vn * col.normalY;
    const vtx = vx - vnx;
    const vty = vy - vny;

    const tangSpeed = Math.sqrt(vtx * vtx + vty * vty);
    const frictionFactor = tangSpeed > 1e-8
      ? Math.max(0, 1 - (friction * Math.abs(vn)) / tangSpeed)
      : 1;

    newVx = -restitution * vnx + vtx * frictionFactor;
    newVy = -restitution * vny + vty * frictionFactor;

    // 回転
    const { slope } = col;
    const tLen = Math.sqrt(1 + slope * slope);
    const stx = 1 / tLen;
    const sty = slope / tLen;
    const tangV = (vtx * frictionFactor) * stx + (vty * frictionFactor) * sty;
    newAngVel = -tangV / radius;

    // 微小速度の減衰
    if (Math.sqrt(newVx ** 2 + newVy ** 2) < 0.02) {
      newVx *= 0.8;
      newVy *= 0.8;
      newAngVel *= 0.8;
    }
  }

  return { x: newX, y: newY, vx: newVx, vy: newVy, angVel: newAngVel };
}

function substep(
  objects: PhysicsObject[],
  functions: FunctionEntry[],
  gravity: number,
  dt: number,
  objectCollisions: boolean
): PhysicsObject[] {
  const n = objects.length;
  if (n === 0) return objects;

  // 各物体の状態をコピー
  const x = new Float64Array(n);
  const y = new Float64Array(n);
  const vx = new Float64Array(n);
  const vy = new Float64Array(n);
  const angVel = new Float64Array(n);
  const angle = new Float64Array(n);

  for (let i = 0; i < n; i++) {
    const obj = objects[i];
    x[i] = obj.x;
    y[i] = obj.y;
    vx[i] = obj.vx;
    vy[i] = obj.vy;
    angVel[i] = obj.angularVelocity;
    angle[i] = obj.angle;
  }

  // Step 1: 重力を速度に加え、位置を更新
  for (let i = 0; i < n; i++) {
    vy[i] -= gravity * dt;
    x[i] += vx[i] * dt;
    y[i] += vy[i] * dt;
    angle[i] += angVel[i] * dt;
  }

  // Step 2: グラフ衝突解決（全物体）
  for (let i = 0; i < n; i++) {
    const result = resolveGraphCollision(
      objects[i], x[i], y[i], vx[i], vy[i], angVel[i], functions
    );
    x[i] = result.x;
    y[i] = result.y;
    vx[i] = result.vx;
    vy[i] = result.vy;
    angVel[i] = result.angVel;
  }

  // Step 3: 物体間衝突解決（複数回イテレーション）
  if (objectCollisions && n >= 2) {
    const ITERS = n <= 2 ? 4 : 3;

    for (let iter = 0; iter < ITERS; iter++) {
      // 各物体のグラフ接触状態を判定
      const onGraph = new Array(n).fill(false);
      for (let i = 0; i < n; i++) {
        const contact = checkGraphContact(objects[i], x[i], y[i], angle[i], functions);
        onGraph[i] = contact.hasContact;
      }

      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          const ra = objectRadiusWorld(objects[i]);
          const rb = objectRadiusWorld(objects[j]);
          const minDist = ra + rb;

          const dx = x[j] - x[i];
          const dy = y[j] - y[i];
          const distSq = dx * dx + dy * dy;

          if (distSq >= minDist * minDist || distSq < 1e-14) continue;

          const dist = Math.sqrt(distSq);
          const nx = dx / dist;
          const ny = dy / dist;
          const overlap = minDist - dist;

          // 下にある方を判定
          // ny > 0 → j が上、i が下
          // ny < 0 → i が上、j が下
          const iIsBelow = ny > 0;
          const jIsBelow = ny < 0;

          // 位置補正
          if (onGraph[i] && onGraph[j]) {
            // 両方グラフ上 → 横方向のみ
            const sepX = nx * overlap * 0.5;
            x[i] -= sepX;
            x[j] += sepX;
          } else if (onGraph[i] && iIsBelow) {
            // i が下で固定 → j だけ上に押し出す
            x[j] += nx * overlap;
            y[j] += ny * overlap;
          } else if (onGraph[j] && jIsBelow) {
            // j が下で固定 → i だけ上に押し出す
            x[i] -= nx * overlap;
            y[i] -= ny * overlap;
          } else {
            // 通常: 質量比で分離
            const ma = objects[i].mass;
            const mb = objects[j].mass;
            const total = ma + mb;
            const corrA = overlap * (mb / total);
            const corrB = overlap * (ma / total);
            x[i] -= nx * corrA;
            y[i] -= ny * corrA;
            x[j] += nx * corrB;
            y[j] += ny * corrB;
          }

          // 速度インパルス
          const relVx = vx[i] - vx[j];
          const relVy = vy[i] - vy[j];
          const relVn = relVx * nx + relVy * ny;

          if (relVn > 0) {
            const e = (objects[i].restitution + objects[j].restitution) / 2;

            let effectiveMa = objects[i].mass;
            let effectiveMb = objects[j].mass;
            if (onGraph[i] && iIsBelow) effectiveMa = Infinity;
            if (onGraph[j] && jIsBelow) effectiveMb = Infinity;

            const denom =
              (effectiveMa === Infinity ? 0 : 1 / effectiveMa) +
              (effectiveMb === Infinity ? 0 : 1 / effectiveMb);

            if (denom > 0) {
              const impMag = -(1 + e) * relVn / denom;
              if (effectiveMa !== Infinity) {
                vx[i] += (impMag / effectiveMa) * nx;
                vy[i] += (impMag / effectiveMa) * ny;
              }
              if (effectiveMb !== Infinity) {
                vx[j] -= (impMag / effectiveMb) * nx;
                vy[j] -= (impMag / effectiveMb) * ny;
              }
            }
          }
        }
      }

      // 各イテレーション後: グラフにめり込んだ物体を再修正
      for (let i = 0; i < n; i++) {
        const result = resolveGraphCollision(
          objects[i], x[i], y[i], vx[i], vy[i], angVel[i], functions
        );
        x[i] = result.x;
        y[i] = result.y;
        vx[i] = result.vx;
        vy[i] = result.vy;
        angVel[i] = result.angVel;
      }
    }
  }

  // Step 4: 最終グラフチェック（安全のため）
  for (let i = 0; i < n; i++) {
    const result = resolveGraphCollision(
      objects[i], x[i], y[i], vx[i], vy[i], angVel[i], functions
    );
    x[i] = result.x;
    y[i] = result.y;
    vx[i] = result.vx;
    vy[i] = result.vy;
    angVel[i] = result.angVel;
  }

  // 結果をオブジェクトに反映
  return objects.map((obj, i) => ({
    ...obj,
    x: x[i],
    y: y[i],
    vx: vx[i],
    vy: vy[i],
    angle: angle[i],
    angularVelocity: angVel[i] * Math.pow(0.993, dt * 60),
  }));
}

// ---- メインステップ ──────────────────────────────────────────────

export function stepPhysics(
  objects: PhysicsObject[],
  functions: FunctionEntry[],
  _settings: GraphSettings,
  gravity: number,
  dt: number,
  _canvasWidth: number,
  _canvasHeight: number,
  objectCollisions: boolean = true
): PhysicsObject[] {
  const SUBSTEPS = getSubsteps(objects.length);
  const subDt = dt / SUBSTEPS;
  let current = [...objects];

  for (let s = 0; s < SUBSTEPS; s++) {
    current = substep(current, functions, gravity, subDt, objectCollisions);
  }

  // 軌跡を更新（配列コピーを最小化）
  return current.map((obj, idx) => {
    const prevTrail = objects[idx].trail;
    const newPoint = { x: obj.x, y: obj.y };
    let newTrail: { x: number; y: number }[];
    if (prevTrail.length >= TRAIL_MAX) {
      newTrail = prevTrail.slice(-(TRAIL_MAX - 1));
      newTrail.push(newPoint);
    } else {
      newTrail = [...prevTrail, newPoint];
    }
    return { ...obj, trail: newTrail };
  });
}